import React, { useState } from "react";
import { Slide, Fade, Zoom } from "react-awesome-reveal";
import {
  TbBuildingFactory2,
  TbCode,
  TbStethoscope,
  TbLayout,
  TbPlugConnected,
  TbSchool,
  TbTrendingUp,
  TbSearch,
  TbMap,
  TbPalette,
  TbRefresh,
  TbTestPipe,
  TbLifebuoy,
} from "react-icons/tb";
import "../../styles/HowWeWork.css";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const tracks = [
  {
    id: "industrial",
    label: "Implementar un sistema industrial",
    icon: <TbBuildingFactory2 size={20} />,
    title: "Implementación ERP / MES / Gemelo Digital",
    subtitle: "Proceso optimizado y modular.",
    keyMessage: "Implementamos rápido. Escalamos por fases. Medimos resultados.",
    accent: "#7c3aed",
    steps: [
      {
        icon: <TbStethoscope size={24} />,
        number: "01",
        title: "Diagnóstico de madurez digital",
        desc: "Evaluamos el estado actual de tus procesos, sistemas y datos para identificar brechas y oportunidades reales.",
      },
      {
        icon: <TbLayout size={24} />,
        number: "02",
        title: "Diseño de arquitectura",
        desc: "Definimos qué módulos necesitás, cómo se integran entre sí y con tus sistemas existentes.",
      },
      {
        icon: <TbPlugConnected size={24} />,
        number: "03",
        title: "Parametrización e integración",
        desc: "Configuramos el sistema a medida de tu operación y lo conectamos con tus fuentes de datos.",
      },
      {
        icon: <TbSchool size={24} />,
        number: "04",
        title: "Capacitación",
        desc: "Entrenamos a tu equipo para que opere con confianza desde el primer día.",
      },
      {
        icon: <TbTrendingUp size={24} />,
        number: "05",
        title: "Optimización continua",
        desc: "Revisamos indicadores contigo, ajustamos y escalamos el sistema a medida que crece tu operación.",
      },
    ],
  },
  {
    id: "custom",
    label: "Desarrollar una solución a medida",
    icon: <TbCode size={20} />,
    title: "Desarrollo de Software a Medida",
    subtitle: "Un proceso con claridad estratégica en cada etapa.",
    keyMessage: "No desarrollamos sin claridad estratégica.",
    accent: "#059669",
    steps: [
      {
        icon: <TbSearch size={24} />,
        number: "01",
        title: "Discovery técnico y funcional",
        desc: "Entendemos el problema real, los usuarios, los flujos y los requisitos técnicos antes de escribir una línea de código.",
      },
      {
        icon: <TbMap size={24} />,
        number: "02",
        title: "Definición de alcance y roadmap",
        desc: "Documentamos el alcance, priorizamos funcionalidades y trazamos un plan de entrega claro y realista.",
      },
      {
        icon: <TbPalette size={24} />,
        number: "03",
        title: "Diseño UX/UI",
        desc: "Diseñamos interfaces que son funcionales, intuitivas y alineadas con tu marca.",
      },
      {
        icon: <TbRefresh size={24} />,
        number: "04",
        title: "Desarrollo por sprints",
        desc: "Construimos en ciclos cortos con entregas verificables, para que tengas visibilidad y control en todo momento.",
      },
      {
        icon: <TbTestPipe size={24} />,
        number: "05",
        title: "Testing y despliegue",
        desc: "Probamos exhaustivamente antes de salir a producción. Nada se despliega sin validación.",
      },
      {
        icon: <TbLifebuoy size={24} />,
        number: "06",
        title: "Soporte y evolución",
        desc: "No desaparecemos después del lanzamiento. Acompañamos el crecimiento y la evolución del producto.",
      },
    ],
  },
];

/* ─────────────────────────────────────────────
   STEP CARD
───────────────────────────────────────────── */
const StepCard = ({ step, index, accent }) => (
  <Zoom delay={index * 80} duration={500} triggerOnce>
    <div className="hww-step" style={{ "--accent": accent }}>
      <div className="hww-step-number">{step.number}</div>
      <div className="hww-step-icon" style={{ color: accent }}>
        {step.icon}
      </div>
      <div className="hww-step-body">
        <h4 className="hww-step-title">{step.title}</h4>
        <p className="hww-step-desc">{step.desc}</p>
      </div>
      {/* Connector line (not on last step) */}
      <div className="hww-step-connector" />
    </div>
  </Zoom>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const HowWeWork = () => {
  const [activeId, setActiveId] = useState(null);
  const active = tracks.find((t) => t.id === activeId);

  return (
    <section className="hww-section" id="metodologia">
      <div className="container py-5">

        {/* ── Header ── */}
        <Slide direction="up" duration={700} triggerOnce>
          <div className="text-center mb-5">
            <span className="eco-badge">Metodología</span>
            <h2 className="eco-main-title text-white mt-3">
              Cómo trabajamos según tu necesidad
            </h2>
            <p className="eco-main-sub text-white-50">
              Elegí el camino que mejor describe lo que estás buscando.
            </p>
          </div>
        </Slide>

        {/* ── Selector buttons ── */}
        <Fade delay={200} duration={700} triggerOnce>
          <div className="hww-selector">
            <p className="hww-selector-label text-white-50">¿Qué estás buscando?</p>
            <div className="hww-selector-btns">
              {tracks.map((t) => (
                <button
                  key={t.id}
                  className={`hww-tab${activeId === t.id ? " hww-tab--active" : ""}`}
                  style={activeId === t.id ? { "--accent": t.accent } : {}}
                  onClick={() => setActiveId(activeId === t.id ? null : t.id)}
                >
                  <span className="hww-tab-icon">{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Fade>

        {/* ── Flow panel ── */}
        {active && (
          <Fade duration={500} triggerOnce key={active.id}>
            <div className="hww-panel" style={{ "--accent": active.accent }}>

              {/* Panel header */}
              <div className="hww-panel-header">
                <div>
                  <h3 className="hww-panel-title">{active.title}</h3>
                  <p className="hww-panel-sub text-white-50">{active.subtitle}</p>
                </div>
                <div className="hww-key-msg" style={{ borderColor: active.accent }}>
                  <span className="hww-key-msg-label">Mensaje clave</span>
                  <p>"{active.keyMessage}"</p>
                </div>
              </div>

              {/* Steps */}
              <div className="hww-steps">
                {active.steps.map((step, i) => (
                  <StepCard key={step.number} step={step} index={i} accent={active.accent} />
                ))}
              </div>

            </div>
          </Fade>
        )}

        {/* Placeholder when nothing selected */}
        {!active && (
          <Fade duration={400} triggerOnce>
            <div className="hww-placeholder">
              <span>↑ Seleccioná una opción para ver el proceso</span>
            </div>
          </Fade>
        )}

      </div>
    </section>
  );
};

export default HowWeWork;
